/**
 * Unified care-type serialization for provider search requests.
 * Public and authenticated paths must produce equivalent query semantics.
 */
import { logWarn } from "../config/env";
import {
  BUSINESS_TYPE_DEFINITIONS,
  BUSINESS_TYPE_SEARCH_PEERS,
  PROVIDER_TYPES,
} from "../generated/careTaxonomy";
import { isServiceLikeSearchBusinessType } from "./searchBusinessTypeGuard";
import { resolveSearchBusinessTypeSlug } from "./caseTypeToBusinessType";
import { mapCareTypeToBusinessTypeKey } from "./careTypeMappings";
import { mapSpecializationToDisplay } from "./specializationMappings";

const SEARCH_PICKLIST_LABELS = new Set(PROVIDER_TYPES.map((option) => option.value));

/** Child storage key → peer parent (first parent wins; home_care → home_health). */
const PEER_PARENT_BY_CHILD: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  for (const [parent, children] of Object.entries(BUSINESS_TYPE_SEARCH_PEERS)) {
    for (const child of children) {
      if (!map.has(child)) {
        map.set(child, parent);
      }
    }
  }
  return map;
})();

export type CareTypeSearchQueryParams = {
  business_type?: string;
  specializations?: string;
  required_core_services?: string;
};

const SLUG_TO_FILTER_LABEL = new Map(
  BUSINESS_TYPE_DEFINITIONS.filter((definition) => definition.searchFilterLabel).map(
    (definition) => [definition.key, definition.searchFilterLabel as string]
  )
);

/** Resolve UI/raw care type to canonical API slug; omit when unmapped. */
export function resolveCareTypeSearchSlug(
  rawBusinessType: string | null | undefined
): string | undefined {
  const trimmed = rawBusinessType?.trim();
  if (!trimmed) {
    return undefined;
  }

  return (
    resolveSearchBusinessTypeSlug(trimmed) ??
    resolveSearchBusinessTypeSlug(mapCareTypeToBusinessTypeKey(trimmed))
  );
}

/** Map canonical slug to search filter display label for URL/store rehydrate. */
export function mapCareTypeSlugToFilterLabel(slug: string): string {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) {
    return "";
  }
  const definition = BUSINESS_TYPE_DEFINITIONS.find(
    (entry) => entry.key === normalized
  );
  const replacedBy = definition?.status === "deprecated" ? definition.replacedBy : null;
  if (typeof replacedBy === "string" && replacedBy) {
    const replacement = BUSINESS_TYPE_DEFINITIONS.find(
      (entry) => entry.key === replacedBy
    );
    const label =
      replacement && "searchFilterLabel" in replacement
        ? replacement.searchFilterLabel
        : null;
    if (label) {
      return label;
    }
  }
  return (
    SLUG_TO_FILTER_LABEL.get(normalized as any) ??
    definition?.fullLabel ??
    mapSpecializationToDisplay(normalized) ??
    slug
  );
}

/**
 * Plan 34: density-hidden picklist labels promote to a searchable peer umbrella
 * (taxonomy-driven via BUSINESS_TYPE_SEARCH_PEERS + PROVIDER_TYPES).
 * Storage affinity labels (e.g. Home Care Agency) are preserved when no umbrella
 * parent is on the picklist (e.g. Adult Foster Care unavailable).
 */
export function coerceCareTypeFilterLabelToSearchPicklist(
  label: string | undefined
): string | undefined {
  const trimmed = label?.trim();
  if (!trimmed) {
    return undefined;
  }
  if (SEARCH_PICKLIST_LABELS.has(trimmed)) {
    return trimmed;
  }

  const slug = resolveCareTypeSearchSlug(trimmed);
  if (!slug) {
    return trimmed;
  }

  const ownLabel = SLUG_TO_FILTER_LABEL.get(slug as any) ?? trimmed;
  if (SEARCH_PICKLIST_LABELS.has(ownLabel)) {
    return ownLabel;
  }

  const parent = PEER_PARENT_BY_CHILD.get(slug);
  if (parent) {
    const parentLabel = SLUG_TO_FILTER_LABEL.get(parent as any);
    if (parentLabel && SEARCH_PICKLIST_LABELS.has(parentLabel)) {
      return parentLabel;
    }
  }
  return ownLabel;
}

/**
 * Storage/API slug for inventory identity (no umbrella promotion).
 * Discovery queries that need peer expansion should use
 * {@link resolveCareTypeSearchDiscoverySlug}.
 */
export function resolveCareTypeSearchDiscoverySlug(
  rawBusinessType: string | null | undefined
): string | undefined {
  const storageSlug = resolveCareTypeSearchSlug(rawBusinessType);
  if (!storageSlug || isServiceLikeSearchBusinessType(storageSlug)) {
    return storageSlug;
  }
  const storageLabel = mapCareTypeSlugToFilterLabel(storageSlug);
  const picklistLabel = coerceCareTypeFilterLabelToSearchPicklist(storageLabel);
  if (!picklistLabel || picklistLabel === storageLabel) {
    return storageSlug;
  }
  return resolveCareTypeSearchSlug(picklistLabel) ?? storageSlug;
}

/** Build equivalent public/auth care-type query params from UI selection. */
export function resolveCareTypeSearchQueryParams(
  rawBusinessType: string | null | undefined
): CareTypeSearchQueryParams | undefined {
  const databaseBusinessType = resolveCareTypeSearchDiscoverySlug(rawBusinessType);
  if (!databaseBusinessType) {
    return undefined;
  }

  if (isServiceLikeSearchBusinessType(databaseBusinessType)) {
    const definition = BUSINESS_TYPE_DEFINITIONS.find(
      (entry) => entry.key === databaseBusinessType
    );
    const label = definition?.fullLabel?.trim();
    if (!label) {
      return undefined;
    }
    return { required_core_services: label };
  }

  return { business_type: databaseBusinessType };
}

/** Append unified care-type params to URLSearchParams. */
export function appendCareTypeSearchParams(
  params: URLSearchParams,
  rawBusinessType: string | null | undefined
): boolean {
  const trimmed = rawBusinessType?.trim();
  if (!trimmed) {
    return false;
  }

  const resolved = resolveCareTypeSearchQueryParams(trimmed);
  if (!resolved) {
    logWarn(
      `Omitted unmapped business_type from search: "${trimmed}"`,
      "API"
    );
    return false;
  }

  if (resolved.business_type) {
    params.append("business_type", resolved.business_type);
  }
  if (resolved.specializations) {
    params.append("specializations", resolved.specializations);
  }
  if (resolved.required_core_services) {
    const label = resolved.required_core_services;
    const existing = params.get("required_core_services");
    if (!existing) {
      params.append("required_core_services", label);
    } else if (
      !existing
        .split(",")
        .some((item) => item.trim().toLowerCase() === label.toLowerCase())
    ) {
      params.set("required_core_services", `${existing},${label}`);
    }
  }
  return true;
}

/** Parse care-type filter label from URL query (`business_type` or service-like `specializations`). */
export function parseCareTypeFilterLabelFromSearchParams(
  searchParams: URLSearchParams
): string | undefined {
  const businessType = searchParams.get("business_type")?.trim();
  if (businessType) {
    return coerceCareTypeFilterLabelToSearchPicklist(
      mapCareTypeSlugToFilterLabel(businessType)
    );
  }

  const specializations = searchParams.get("specializations")?.trim();
  if (specializations) {
    const firstSlug = specializations.split(",")[0]?.trim();
    if (firstSlug) {
      const fromSlug = coerceCareTypeFilterLabelToSearchPicklist(
        mapCareTypeSlugToFilterLabel(firstSlug)
      );
      if (fromSlug) {
        return fromSlug;
      }
    }
  }

  const requiredServices = searchParams.get("required_core_services")?.trim();
  if (!requiredServices) {
    return undefined;
  }
  for (const part of requiredServices.split(",")) {
    const label = part.trim();
    const match = BUSINESS_TYPE_DEFINITIONS.find(
      (definition) =>
        isServiceLikeSearchBusinessType(definition.key) &&
        (definition.fullLabel === label || definition.searchFilterLabel === label)
    );
    if (match) {
      return coerceCareTypeFilterLabelToSearchPicklist(
        mapCareTypeSlugToFilterLabel(match.key)
      );
    }
  }
  return undefined;
}
